import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import { useFavoritesStore } from '../../store/favorites.store';
import { useCartStore } from '../../store/cart.store';
import { Heart, Package, ShoppingCart, Trash2, Tag } from 'lucide-react';
import { getFirstProductImage } from '../../utils/product-images';
import { capitalizeProductName } from '../../utils/capitalize';
import toast from 'react-hot-toast';

export const FavoritosPage: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { items: favoritos, removeFavorite } = useFavoritesStore();
  const { addItem } = useCartStore();

  const handleAddToCart = (producto: typeof favoritos[0]) => {
    if (producto.stock <= 0) {
      toast.error('Producto agotado');
      return;
    }
    addItem({
      id: producto.id,
      nombre: producto.nombre,
      precio: producto.precio,
      imagen: getFirstProductImage(producto.imagenes),
      receta: producto.requiereReceta,
      categoria: producto.categoria,
    });
    toast.success(`${producto.nombre} agregado al carrito`);
  };

  return (
    <div className="p-6 flex flex-col gap-4 h-[calc(100vh-5rem)]">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-shrink-0">
        <div>
          <h1 className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Productos Favoritos
          </h1>
          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {favoritos.length} producto{favoritos.length !== 1 ? 's' : ''} guardado
            {favoritos.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Contenido scrollable */}
      <div className="flex-1 overflow-y-auto min-h-0 space-y-4">
        {favoritos.length === 0 ? (
          <div className={`text-center py-16 rounded-xl border-2 border-dashed ${
            theme === 'dark' ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'
          }`}>
            <div className="flex justify-center mb-4">
              <div className={`p-4 rounded-full ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'
              }`}>
                <Heart className="w-12 h-12 opacity-50" />
              </div>
            </div>
            <h3 className="text-xl font-medium mb-2">No tienes favoritos aún</h3>
            <p className="mb-6">
              Explora nuestro catálogo y marca tus productos favoritos para encontrarlos fácilmente más tarde
            </p>
            <div className="space-y-4">
              <button
                onClick={() => navigate('/cliente/compras')}
                className={`inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                  theme === 'dark'
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                }`}
              >
                <ShoppingCart className="w-5 h-5" />
                <span>Ir a Compras</span>
              </button>
              <div className="flex items-center justify-center space-x-2 text-sm">
                <Package className="w-4 h-4" />
                <span>Más de 1000 productos disponibles</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {favoritos.map((producto) => (
              <div
                key={producto.id}
                className={`border rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg ${
                  theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}
              >
                {/* Imagen */}
                <div className={`relative h-40 overflow-hidden ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                }`}>
                  {getFirstProductImage(producto.imagenes) ? (
                    <img
                      src={getFirstProductImage(producto.imagenes)}
                      alt={producto.nombre}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                  <button
                    onClick={() => removeFavorite(producto.id)}
                    className={`absolute top-3 right-3 p-2 rounded-full transition-colors ${
                      theme === 'dark'
                        ? 'bg-gray-800/80 text-red-400 hover:bg-gray-700'
                        : 'bg-white/80 text-red-500 hover:bg-red-50'
                    }`}
                    title="Quitar de favoritos"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Contenido */}
                <div className="p-4">
                  <h3 className={`font-semibold text-lg line-clamp-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {capitalizeProductName(producto.nombre)}
                  </h3>
                  <div className="flex items-center space-x-2 mt-1">
                    <Tag className="w-3 h-3 text-gray-400" />
                    <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {producto.categoria}
                    </span>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <div>
                      <p className={`text-xl font-bold ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        ${producto.precio.toLocaleString('es-CO')}
                      </p>
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Stock: {producto.stock > 0 ? producto.stock : 'Agotado'}
                      </p>
                    </div>
                    <button
                      onClick={() => handleAddToCart(producto)}
                      disabled={producto.stock <= 0}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                        producto.stock > 0
                          ? theme === 'dark'
                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                            : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <ShoppingCart className="w-4 h-4" />
                      <span>Agregar</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
